import {SavedLegend, SavedLegendItem} from 'common/report/constants';
import {charts} from 'common/util/colors';

const defaultColorPool = charts.default.map((color) => color.primary);
const defaultOverallReservedColor = defaultColorPool[0];
export type ColorChoice = {key: string; color: string};

function* cycle(arr: string[]) {
  while (true) {
    for (const item of arr) {
      yield item;
    }
  }
}

function emptyFrequencyMap(keys: string[]): Map<string, number> {
  return new Map(keys.map((k) => [k, 0] as [string, number]));
}

/**
 * Pick less used colors for new items.
 *
 * Assumes that the difference in used color frequencies is <= 1.
 */
export function maybeAssignColors(
  lastState: ColorChoice[],
  newItems: string[],
  colorPool: string[] = defaultColorPool,
  overallReservedColor: string = defaultOverallReservedColor,
): ColorChoice[] {
  const usedColorMap = new Map(
    // In case of older bookmarks, filter out any segment that
    // used the overallReservedColor from having its colors carry over.
    lastState
      .filter((c) => newItems.includes(c.key) && c.color !== overallReservedColor)
      .map((c) => [c.key, c.color] as [string, string]),
  );

  const usedColorFrequency = emptyFrequencyMap(colorPool.filter((item) => item !== overallReservedColor));
  usedColorMap.forEach((value) => {
    usedColorFrequency.set(value, usedColorFrequency.get(value) + 1);
  });

  const sortedColors = [...usedColorFrequency.keys()].sort((aColor, bColor) => {
    return usedColorFrequency.get(aColor) - usedColorFrequency.get(bColor);
  });

  const colors = cycle(sortedColors);
  return newItems.map((item) => {
    // Overall always gets the same color, the other segments are cycled
    if (item === `$overall`) {
      return {color: overallReservedColor, key: item};
    }
    const existing = usedColorMap.get(item);
    if (existing) {
      return {color: existing, key: item};
    } else {
      return {color: colors.next().value as string, key: item};
    }
  });
}

export function convertOldSavedSegments(colorChoices: ColorChoice[]): SavedLegend[] {
  const savedLegendArray = new Array<SavedLegend>();
  const segmentSets = new Array<Set<string>>();
  for (const choice of colorChoices) {
    if (choice.key !== `$overall`) {
      const choiceSplit = choice.key.split(`/`).map((item) => item.trim());
      choiceSplit.forEach((split, index) => {
        if (segmentSets[index] === undefined) {
          const newSet = new Set<string>();
          segmentSets.push(newSet);
        }
        segmentSets[index].add(split);
      });
    }
  }
  for (const segmentSet of segmentSets) {
    const savedLegend: SavedLegend = {selected: new Array<SavedLegendItem>()};
    let colorIndex = 1;
    const isFinalSegment = segmentSets[segmentSets.length - 1] === segmentSet;
    segmentSet.forEach((segment) => {
      const legendItem: SavedLegendItem = {key: segment, color: colorIndex};
      savedLegend.selected.push(legendItem);
      if (isFinalSegment) {
        colorIndex = colorIndex >= defaultColorPool.length ? 1 : colorIndex + 1;
      }
    });
    savedLegendArray.push(savedLegend);
  }
  return savedLegendArray.length > 0 ? savedLegendArray : null;
}
